class ProductBannerCarousel {
  constructor(carousel) {
    this.carousel = carousel;

    if (!this.carousel) return;

    this.section = this.carousel.closest('.shopify-section') || this.carousel;
    this.mediaSlides = this.carousel.querySelectorAll('.js-product-banner-media-slide');
    this.textSlides = this.carousel.querySelectorAll('.js-product-banner-text-slide');

    this.nav = this.carousel.querySelector('.product-banner-carousel__nav');
    this.prevButton = this.carousel.querySelector('.js-product-banner-carousel-prev');
    this.nextButton = this.carousel.querySelector('.js-product-banner-carousel-next');
    this.currentCounter = this.carousel.querySelector('.js-current-slide');

    this.index = 0;
    this.total = this.textSlides.length;
    this.isAnimating = false;

    this.handleResize = this.handleResize.bind(this);
    this.handleBlockSelect = this.handleBlockSelect.bind(this);
    this.handleSectionLoad = this.handleSectionLoad.bind(this);

    this.init();
  }

  init() {
    if (!this.total) return;

    this.setupSlides();
    this.bindEvents();
    this.setFirstSlideHeightToTallest();
    this.goTo(0, true);

    window.addEventListener('resize', this.handleResize);

    if (window.Shopify && Shopify.designMode) {
      document.addEventListener('shopify:block:select', this.handleBlockSelect);
      document.addEventListener('shopify:section:load', this.handleSectionLoad);
    }
  }

  handleResize() {
    this.setFirstSlideHeightToTallest();
  }

  handleSectionLoad(event) {
    if (!event.target) return;
    if (!event.target.contains(this.carousel) && event.target !== this.section) return;

    this.setFirstSlideHeightToTallest();
    this.goTo(this.index, true);
  }

  handleBlockSelect(event) {
    if (!event.target) return;
    if (!this.carousel.contains(event.target)) return;

    const selectedBlock = event.target.closest('[data-slide-index]');
    if (!selectedBlock) return;

    const slideIndex = Number(selectedBlock.getAttribute('data-slide-index'));
    if (Number.isNaN(slideIndex)) return;

    this.goTo(slideIndex, true);

    const sectionTopOffset = 120;
    const rect = this.section.getBoundingClientRect();
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const targetTop = rect.top + scrollTop - sectionTopOffset;

    window.scrollTo({
      top: targetTop,
      behavior: 'smooth'
    });
  }

  setFirstSlideHeightToTallest() {
    if (!this.textSlides.length) return;

    const firstSlide = this.carousel.querySelector('.js-product-banner-text-slide[data-slide-index="0"]');
    if (!firstSlide) return;

    let tallestHeight = 0;

    this.textSlides.forEach((slide) => {
      const previousDisplay = slide.style.display;
      const previousVisibility = slide.style.visibility;
      const previousPosition = slide.style.position;
      const previousHeight = slide.style.height;

      slide.style.display = 'block';
      slide.style.visibility = 'hidden';
      slide.style.position = 'absolute';
      slide.style.height = 'auto';

      const slideHeight = slide.offsetHeight;
      if (slideHeight > tallestHeight) {
        tallestHeight = slideHeight;
      }

      slide.style.display = previousDisplay;
      slide.style.visibility = previousVisibility;
      slide.style.position = previousPosition;
      slide.style.height = previousHeight;
    });

    firstSlide.style.height = `${tallestHeight}px`;
  }

  setupSlides() {
    this.mediaSlides.forEach((slide, index) => {
      slide.classList.remove('is-active', 'is-entering', 'is-leaving');
      slide.classList.toggle('is-active', index === 0);
    });

    this.textSlides.forEach((slide, index) => {
      slide.classList.remove('is-active', 'is-entering', 'is-leaving');
      slide.classList.toggle('is-active', index === 0);
    });

    this.updateCounter();
    this.updateButtons();
  }

  bindEvents() {
    if (this.prevButton) {
      this.prevButton.addEventListener('click', () => {
        if (this.isAnimating) return;
        this.prev();
      });
    }

    if (this.nextButton) {
      this.nextButton.addEventListener('click', () => {
        if (this.isAnimating) return;
        this.next();
      });
    }
  }

  updateButtons() {
    // Both arrows show whenever there is more than one slide, because navigation wraps
    // in both directions. A single slide has nothing to navigate to.
    const hasMultipleSlides = this.total > 1;

    if (this.nav) {
      this.nav.classList.toggle('d-none', !hasMultipleSlides);
    }

    if (this.prevButton) {
      this.prevButton.classList.toggle('d-none', !hasMultipleSlides);
    }

    if (this.nextButton) {
      this.nextButton.classList.toggle('d-none', !hasMultipleSlides);
    }
  }

  updateCounter() {
    if (this.currentCounter) {
      this.currentCounter.textContent = this.index + 1;
    }
  }

  goTo(index, immediate = false) {
    if (index < 0 || index >= this.total) return;
    if (index === this.index && !immediate) return;
    if (this.isAnimating && !immediate) return;

    const previousIndex = this.index;
    const nextIndex = index;

    const currentMediaSlide = this.mediaSlides[previousIndex];
    const nextMediaSlide = this.mediaSlides[nextIndex];

    const currentTextSlide = this.textSlides[previousIndex];
    const nextTextSlide = this.textSlides[nextIndex];

    const currentMediaImg = currentMediaSlide ? currentMediaSlide.querySelector('img') : null;
    const nextMediaImg = nextMediaSlide ? nextMediaSlide.querySelector('img') : null;

    const shouldAnimateMedia = !!currentMediaImg && !!nextMediaImg;

    this.index = nextIndex;
    this.updateCounter();
    this.updateButtons();

    if (immediate) {
      this.mediaSlides.forEach((slide, slideIndex) => {
        slide.classList.remove('is-entering', 'is-leaving');
        slide.classList.toggle('is-active', slideIndex === this.index);
      });

      this.textSlides.forEach((slide, slideIndex) => {
        slide.classList.remove('is-entering', 'is-leaving');
        slide.classList.toggle('is-active', slideIndex === this.index);
      });

      return;
    }

    this.isAnimating = true;

    if (shouldAnimateMedia) {
      if (currentMediaSlide) {
        currentMediaSlide.classList.remove('is-active', 'is-entering');
        currentMediaSlide.classList.add('is-leaving');
      }

      if (nextMediaSlide) {
        nextMediaSlide.classList.remove('is-leaving', 'is-active');
        nextMediaSlide.classList.add('is-entering');
      }

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (nextMediaSlide) {
            nextMediaSlide.classList.add('is-active');
          }
        });
      });
    }

    if (currentTextSlide) {
      currentTextSlide.classList.remove('is-active', 'is-entering');
      currentTextSlide.classList.add('is-leaving');
    }

    if (nextTextSlide) {
      nextTextSlide.classList.remove('is-leaving', 'is-active');
      nextTextSlide.classList.add('is-entering');
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (nextTextSlide) {
          nextTextSlide.classList.add('is-active');
        }
      });
    });

    setTimeout(() => {
      if (shouldAnimateMedia) {
        this.mediaSlides.forEach((slide, slideIndex) => {
          slide.classList.remove('is-entering', 'is-leaving');
          slide.classList.toggle('is-active', slideIndex === this.index);
        });
      }

      this.textSlides.forEach((slide, slideIndex) => {
        slide.classList.remove('is-entering', 'is-leaving');
        slide.classList.toggle('is-active', slideIndex === this.index);
      });

      this.isAnimating = false;
    }, 500);
  }

  next() {
    if (this.total <= 1) return;
    this.goTo((this.index + 1) % this.total);
  }

  prev() {
    if (this.total <= 1) return;
    this.goTo((this.index - 1 + this.total) % this.total);
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const carousels = document.querySelectorAll('.js-product-banner-carousel__inner, .product-banner-carousel__inner.js-product-banner-carousel');

  carousels.forEach((carousel) => {
    new ProductBannerCarousel(carousel);
  });
});